import { demoApi } from "./demoLocale";
type StatisticsRequest = {
  ProfileId?: string;
  AcademyId?: string;
  GroupOwnerId?: string;
  GroupId?: string;
  StartAt?: string;
  EndAt?: string;
  Interval?: number;
  Activity?: number;
};

/** Pierwsza drużyna — dane 1:1 ze screena (po remapowaniu dni). */
const BASE_GROUP_STATS_FOLDER_ID = "00000000-0000-0000-0000-000000001601";

type DayMetricTemplate = {
  dayOffset: number;
  activity: 0 | 4;
  Distance: number;
  HSR: number;
  Sprint: number;
  Acceleration: number;
  Deceleration: number;
  PlayerLoad: number;
  Tempo: number;
};

/**
 * Bazowy tydzień ze screena:
 * - trening wtorkowy → środa
 * - trening czwartkowy → piątek
 * - mecz w środę usunięty
 * - poniedziałek i niedziela bez zmian
 */
const REMAPPED_WEEK_TEMPLATE: DayMetricTemplate[] = [
  {
    dayOffset: 0,
    activity: 0,
    Distance: 7270,
    HSR: 70,
    Sprint: 0,
    Acceleration: 19,
    Deceleration: 27,
    PlayerLoad: 366,
    Tempo: 1.18,
  },
  {
    dayOffset: 2,
    activity: 0,
    Distance: 5826,
    HSR: 33,
    Sprint: 0,
    Acceleration: 19,
    Deceleration: 39,
    PlayerLoad: 347,
    Tempo: 1.05,
  },
  {
    dayOffset: 4,
    activity: 0,
    Distance: 5205,
    HSR: 78,
    Sprint: 0,
    Acceleration: 26,
    Deceleration: 28,
    PlayerLoad: 323,
    Tempo: 0.98,
  },
  {
    dayOffset: 6,
    activity: 4,
    Distance: 7359,
    HSR: 128,
    Sprint: 9,
    Acceleration: 21,
    Deceleration: 33,
    PlayerLoad: 404,
    Tempo: 1.32,
  },
];

const parseDate = (value?: string) => {
  if (!value) {
    return new Date();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const hashSeed = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
};

/** Deterministic 0..1 from seed. */
const unitFromSeed = (key: string) => (hashSeed(key) % 10000) / 10000;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Niezależna intensywność per metryka — słupki nie idą wszystkie w tym samym kierunku. */
const metricIntensity = (slotKey: string, metric: string, t: number) => {
  const phase = unitFromSeed(`${slotKey}|${metric}|ph`) * Math.PI * 2;
  const wave = 0.5 + 0.5 * Math.sin(t * Math.PI * 2.3 + phase);
  const noise = (unitFromSeed(`${slotKey}|${metric}|n`) - 0.5) * 0.48;
  return Math.min(0.97, Math.max(0.12, wave * 0.5 + 0.28 + noise));
};

/**
 * ACWR z osobną fazą per metryka — gdy jedna linia rośnie, inna może spadać.
 * fixedPhaseOffset: 0 / π/2 / π / 3π/2 daje wyraźne rozjechanie linii.
 */
const independentAcwr = (
  slotKey: string,
  metric: string,
  t: number,
  fixedPhaseOffset: number
) => {
  const jitter = (unitFromSeed(`${slotKey}|${metric}|pj`) - 0.5) * 0.7;
  const wave = Math.sin(t * Math.PI * 2 + fixedPhaseOffset + jitter);
  const noise = (unitFromSeed(`${slotKey}|${metric}|an`) - 0.5) * 0.2;
  return Number(Math.min(1.42, Math.max(0.58, 1.0 + wave * 0.3 + noise)).toFixed(2));
};

/** Deterministic jitter ±20…50 for a seed/metric/day. */
const metricJitter = (seedKey: string, metric: string, dayOffset: number) => {
  const seed = hashSeed(`${seedKey}|${metric}|${dayOffset}`);
  const magnitude = 20 + (seed % 31);
  return seed % 2 === 0 ? magnitude : -magnitude;
};

const applyJitter = (
  value: number,
  groupId: string,
  weekKey: string,
  metric: string,
  dayOffset: number,
  exactGroup: boolean
) => {
  // Każdy tydzień lekko inny (±20–50), grupy inne niż bazowa dostają dodatkowo własny offset.
  let next = Math.max(0, Math.round(value + metricJitter(`week:${weekKey}`, metric, dayOffset)));
  if (!exactGroup) {
    next = Math.max(0, Math.round(next + metricJitter(groupId, metric, dayOffset)));
  }
  return next;
};

const toIsoDateOnly = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const mondayOf = (date: Date) => {
  const result = new Date(date);
  const day = (result.getDay() + 6) % 7;
  result.setHours(12, 0, 0, 0);
  result.setDate(result.getDate() - day);
  return result;
};

/**
 * Dane pod detal zawodnika → Statystyki (wykres).
 * Umiarkowane, lekko falujące wartości — metryki niezależne między dniami.
 */
const buildStatistics = (
  slotKey: string,
  index: number,
  total: number,
  benchmark = false
) => {
  const t = total <= 1 ? 0.5 : index / (total - 1);
  const scale = benchmark ? 0.9 : 1;
  const mi = (metric: string) => metricIntensity(slotKey, metric, t);

  const DistanceTotal = Math.round(lerp(2800, 5200, mi("Distance")) * scale);
  const DistanceMdn = Math.round(DistanceTotal * 0.9);
  const HsrTotal = Math.round(lerp(18, 65, mi("HSR")) * scale);
  const HmldTotal = Math.round(lerp(850, 1500, mi("HMLD")) * scale);
  const RunTotal = Math.round(lerp(1000, 1850, mi("Run")) * scale);
  const Load = Math.round(lerp(250, 380, mi("Load")) * scale);
  const AccelerationTotal = Math.round(lerp(24, 48, mi("Acceleration")) * scale);
  const DecelerationTotal = Math.round(lerp(20, 42, mi("Deceleration")) * scale);
  const SprintsTotal = Math.round(lerp(5, 16, mi("Sprint")) * scale);
  const BurnCalories = Math.round(lerp(340, 520, mi("Calories")) * scale);
  const Power = Math.round(lerp(220, 295, mi("Power")) * scale);
  const MaxSpeed = Number(
    (lerp(26.5, 31.0, mi("MaxSpeed")) * (benchmark ? 0.95 : 1)).toFixed(2)
  );
  const SpeedAverage = Number((MaxSpeed * lerp(0.72, 0.86, mi("SpeedAvg"))).toFixed(2));
  const Tempo = Number(lerp(0.94, 1.26, mi("Tempo")).toFixed(2));

  return {
    MaxSpeed: MaxSpeed.toFixed(2),
    MaxSpeedMdn: (MaxSpeed * 0.92).toFixed(2),
    SpeedAverage: SpeedAverage.toFixed(2),
    SpeedAverageMdn: (SpeedAverage * 0.93).toFixed(2),
    DistanceTotal,
    DistanceMdn,
    HeartRateAverage: Math.round(lerp(128, 152, mi("HR"))),
    HsrTotal,
    HsrPercentage: Math.min(24, Math.round(lerp(8, 18, mi("HSR%")))),
    HsrMdn: Math.round(HsrTotal * 0.88),
    HsrPercentageMdn: Math.min(22, Math.round(lerp(7, 16, mi("HSR%M")))),
    HmldTotal,
    HmldPercentage: Math.min(32, Math.round(lerp(14, 26, mi("HMLD%")))),
    HmldMdn: Math.round(HmldTotal * 0.9),
    HmldPercentageMdn: Math.min(30, Math.round(lerp(13, 24, mi("HMLD%M")))),
    BurnCalories,
    RunTotal,
    RunPercentage: Math.min(40, Math.round(lerp(18, 34, mi("Run%")))),
    RunMdn: Math.round(RunTotal * 0.9),
    RunPercentageMdn: Math.min(38, Math.round(lerp(16, 32, mi("Run%M")))),
    SprintsTotal,
    SprintDistanceMdn: Math.round(lerp(140, 260, mi("SprintDist")) * scale),
    IntensityPercentage: Math.min(90, Math.round(lerp(52, 82, mi("Int%")))),
    DynamicsPercentage: Math.min(90, Math.round(lerp(50, 80, mi("Dyn%")))),
    EndurancePercentage: Math.min(90, Math.round(lerp(48, 78, mi("End%")))),
    Load,
    LoadMdn: Math.round(Load * 0.9),
    Tempo,
    TempoMdn: Number((Tempo * 0.93).toFixed(2)),
    AccelerationTotal,
    AccelerationMdn: Math.round(AccelerationTotal * 0.9),
    DecelerationTotal,
    DecelerationMdn: Math.round(DecelerationTotal * 0.9),
    Power,
    TTS: Number(lerp(10.5, 13.2, mi("TTS")).toFixed(1)),
  };
};

const buildGroupMedian = (index: number, benchmark = false) => {
  const offset = benchmark ? -0.12 : 0;
  return {
    Distance: Math.round(4200 + index * 220 + offset * 400),
    DistanceACWR: Number((0.85 + index * 0.04 + offset).toFixed(2)),
    AccelerationACWR: Number((0.9 + index * 0.03 + offset).toFixed(2)),
    DecelerationACWR: Number((0.88 + index * 0.03 + offset).toFixed(2)),
    MaxSpeed: Number((28.5 + index * 0.35 + offset * 2).toFixed(2)),
    HeartRateAverage: Math.round(132 + index * 2 + offset * 10),
    Acceleration: Math.round(42 + index * 2),
    Sprint: Math.round(16 + index),
    Deceleration: Math.round(38 + index * 2),
    HSR: Math.round(820 + index * 55),
    HmldACWR: Number((0.92 + index * 0.02 + offset).toFixed(2)),
    HMLD: Math.round(1250 + index * 70),
    Power: Math.round(260 + index * 8),
    BurnCalories: Math.round(480 + index * 22),
    IntensityPercentage: Math.min(98, 62 + index * 2),
    DynamicsPercentage: Math.min(98, 66 + index * 2),
    EndurancePercentage: Math.min(98, 58 + index * 2),
    PlayerLoad: Math.round(410 + index * 14),
    Tempo: Number((1.2 + index * 0.04).toFixed(2)),
  };
};

const buildGroupPercentage = (index: number) => ({
  Distance: Math.min(100, 55 + index * 3),
  DistanceACWR: Math.min(100, 48 + index * 3),
  AccelerationACWR: Math.min(100, 52 + index * 2),
  DecelerationACWR: Math.min(100, 50 + index * 2),
  MaxSpeed: Math.min(100, 60 + index * 2),
  HeartRateAverage: Math.min(100, 58 + index * 2),
  Acceleration: Math.min(100, 54 + index * 3),
  Sprint: Math.min(100, 45 + index * 3),
  Deceleration: Math.min(100, 50 + index * 2),
  HSR: Math.min(100, 40 + index * 4),
  HmldACWR: Math.min(100, 46 + index * 3),
  HMLD: Math.min(100, 42 + index * 3),
  Power: Math.min(100, 57 + index * 2),
  BurnCalories: Math.min(100, 53 + index * 2),
  IntensityPercentage: Math.min(100, 62 + index * 2),
  DynamicsPercentage: Math.min(100, 66 + index * 2),
  EndurancePercentage: Math.min(100, 58 + index * 2),
  PlayerLoad: Math.min(100, 51 + index * 3),
  Tempo: Math.min(100, 49 + index * 2),
});

const buildDayMedian = (
  day: DayMetricTemplate,
  groupId: string,
  weekKey: string,
  exactGroup: boolean
) => {
  const Distance = applyJitter(
    day.Distance,
    groupId,
    weekKey,
    "Distance",
    day.dayOffset,
    exactGroup
  );
  const HSR = applyJitter(day.HSR, groupId, weekKey, "HSR", day.dayOffset, exactGroup);
  const Sprint = applyJitter(day.Sprint, groupId, weekKey, "Sprint", day.dayOffset, exactGroup);
  const Acceleration = applyJitter(
    day.Acceleration,
    groupId,
    weekKey,
    "Acceleration",
    day.dayOffset,
    exactGroup
  );
  const Deceleration = applyJitter(
    day.Deceleration,
    groupId,
    weekKey,
    "Deceleration",
    day.dayOffset,
    exactGroup
  );
  const PlayerLoad = applyJitter(
    day.PlayerLoad,
    groupId,
    weekKey,
    "PlayerLoad",
    day.dayOffset,
    exactGroup
  );

  const weekTempoDelta = metricJitter(`week:${weekKey}`, "Tempo", day.dayOffset) / 100;
  const groupTempoDelta = exactGroup
    ? 0
    : metricJitter(groupId, "Tempo", day.dayOffset) / 100;
  const TempoBase = Number(
    Math.max(0.5, day.Tempo + weekTempoDelta + groupTempoDelta).toFixed(2)
  );

  return {
    Distance,
    DistanceACWR: Number(
      (
        0.9 +
        day.dayOffset * 0.02 +
        metricJitter(`week:${weekKey}`, "DistanceACWR", day.dayOffset) / 500
      ).toFixed(2)
    ),
    AccelerationACWR: Number(
      (
        0.88 +
        day.dayOffset * 0.01 +
        metricJitter(`week:${weekKey}`, "AccelerationACWR", day.dayOffset) / 500
      ).toFixed(2)
    ),
    DecelerationACWR: Number(
      (
        0.86 +
        day.dayOffset * 0.01 +
        metricJitter(`week:${weekKey}`, "DecelerationACWR", day.dayOffset) / 500
      ).toFixed(2)
    ),
    MaxSpeed: Number((28 + day.dayOffset * 0.2 + (exactGroup ? 0 : 0.3)).toFixed(2)),
    HeartRateAverage: 140 + day.dayOffset * 2,
    Acceleration,
    Sprint,
    Deceleration,
    HSR,
    HmldACWR: Number(
      (
        0.91 +
        day.dayOffset * 0.01 +
        metricJitter(`week:${weekKey}`, "HmldACWR", day.dayOffset) / 500
      ).toFixed(2)
    ),
    HMLD: Math.round(HSR * 1.4 + Acceleration * 8),
    Power: Math.round(250 + PlayerLoad * 0.15),
    BurnCalories: Math.round(Distance * 0.08),
    IntensityPercentage: Math.min(98, 60 + Math.round(HSR / 10)),
    DynamicsPercentage: Math.min(98, 58 + Acceleration),
    EndurancePercentage: Math.min(98, 55 + Math.round(PlayerLoad / 20)),
    PlayerLoad,
    Tempo: TempoBase,
  };
};

const buildDayPercentage = (median: ReturnType<typeof buildDayMedian>) => ({
  Distance: Math.min(100, Math.round(median.Distance / 80)),
  DistanceACWR: Math.min(100, Math.round(median.DistanceACWR * 50)),
  AccelerationACWR: Math.min(100, Math.round(median.AccelerationACWR * 50)),
  DecelerationACWR: Math.min(100, Math.round(median.DecelerationACWR * 50)),
  MaxSpeed: Math.min(100, Math.round(median.MaxSpeed * 2.5)),
  HeartRateAverage: Math.min(100, Math.round(median.HeartRateAverage / 2)),
  Acceleration: Math.min(100, Math.round(median.Acceleration * 2.5)),
  Sprint: Math.min(100, Math.round(median.Sprint * 8)),
  Deceleration: Math.min(100, Math.round(median.Deceleration * 2)),
  HSR: Math.min(100, Math.round(median.HSR / 2)),
  HmldACWR: Math.min(100, Math.round(median.HmldACWR * 50)),
  HMLD: Math.min(100, Math.round(median.HMLD / 20)),
  Power: Math.min(100, Math.round(median.Power / 5)),
  BurnCalories: Math.min(100, Math.round(median.BurnCalories / 8)),
  IntensityPercentage: median.IntensityPercentage,
  DynamicsPercentage: median.DynamicsPercentage,
  EndurancePercentage: median.EndurancePercentage,
  PlayerLoad: Math.min(100, Math.round(median.PlayerLoad / 5)),
  Tempo: Math.min(100, Math.round(median.Tempo * 40)),
});

const buildWeeklyRemappedPoints = (body: StatisticsRequest = {}, benchmark = false) => {
  const start = mondayOf(parseDate(body.StartAt));
  const weekKey = toIsoDateOnly(start);
  const groupId = String(body.GroupId || "");
  const exactGroup = !groupId || groupId === BASE_GROUP_STATS_FOLDER_ID;

  return REMAPPED_WEEK_TEMPLATE.map((day) => {
    const dayDate = new Date(start);
    dayDate.setDate(start.getDate() + day.dayOffset);
    const iso = toIsoDateOnly(dayDate);
    const median = buildDayMedian(
      day,
      groupId || BASE_GROUP_STATS_FOLDER_ID,
      weekKey,
      exactGroup
    );
    if (benchmark) {
      median.Distance = Math.round(median.Distance * 0.92);
      median.PlayerLoad = Math.round(median.PlayerLoad * 0.9);
    }

    return {
      StartAt: `${iso}T00:00:00.000Z`,
      EndAt: `${iso}T23:59:59.000Z`,
      SessionCount: 1,
      Median: median,
      Percentage: buildDayPercentage(median),
      Events: { Activity: day.activity },
      Event: { Activity: day.activity },
    };
  });
};

const buildPoints = (body: StatisticsRequest = {}, benchmark = false) => {
  const start = parseDate(body.StartAt);
  const end = parseDate(body.EndAt);
  const interval = Number(body.Interval);
  const profileKey = String(body.ProfileId || body.GroupOwnerId || "player");

  const pushPoint = (iso: string, index: number, total: number) => {
    const slotKey = `${profileKey}|${iso}|${benchmark ? "b" : "p"}`;
    return {
      StartAt: `${iso}T00:00:00.000Z`,
      EndAt: `${iso}T23:59:59.000Z`,
      SessionCount: 1 + (hashSeed(slotKey) % 2),
      Statistics: buildStatistics(slotKey, index, total, benchmark),
    };
  };

  // season (Interval=2) — punkt na miesiąc
  if (interval === 2) {
    const slots: string[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1, 12);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1, 12);
    while (cursor <= endMonth && slots.length < 18) {
      slots.push(toIsoDateOnly(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    if (slots.length === 0) {
      const fallback = toIsoDateOnly(new Date());
      return [pushPoint(fallback, 0, 1)];
    }
    return slots.map((iso, index) => pushPoint(iso, index, slots.length));
  }

  // fewWeeks (Interval=1) — punkt na tydzień
  if (interval === 1) {
    const slots: string[] = [];
    const cursor = mondayOf(start);
    while (cursor <= end && slots.length < 24) {
      slots.push(toIsoDateOnly(cursor));
      cursor.setDate(cursor.getDate() + 7);
    }
    if (slots.length === 0) {
      const fallback = toIsoDateOnly(new Date());
      return [pushPoint(fallback, 0, 1)];
    }
    return slots.map((iso, index) => pushPoint(iso, index, slots.length));
  }

  // day / week / month — punkt na każdy dzień w zakresie
  const daySlots: string[] = [];
  const dayCursor = new Date(start);
  dayCursor.setHours(12, 0, 0, 0);
  const dayEnd = new Date(end);
  dayEnd.setHours(12, 0, 0, 0);
  while (dayCursor <= dayEnd && daySlots.length < 40) {
    daySlots.push(toIsoDateOnly(dayCursor));
    dayCursor.setDate(dayCursor.getDate() + 1);
  }

  if (daySlots.length === 0) {
    const fallback = toIsoDateOnly(new Date());
    return [pushPoint(fallback, 0, 1)];
  }

  return daySlots.map((iso, index) => pushPoint(iso, index, daySlots.length));
};

const buildGroupPoints = (body: StatisticsRequest = {}, benchmark = false) => {
  const start = parseDate(body.StartAt);
  const end = parseDate(body.EndAt);
  const daysSpan = Math.round((end.getTime() - start.getTime()) / 86400000);
  const interval = Number(body.Interval);

  // fewWeeks (Interval=1) — jeden punkt na każdy tydzień w zakresie
  if (interval === 1) {
    const points: Array<{
      StartAt: string;
      EndAt: string;
      SessionCount: number;
      Median: ReturnType<typeof buildGroupMedian>;
      Percentage: ReturnType<typeof buildGroupPercentage>;
      Events?: { Activity: number };
      Event?: { Activity: number };
    }> = [];

    const cursor = mondayOf(start);
    let index = 0;
    while (cursor <= end && index < 24) {
      const weekKey = toIsoDateOnly(cursor);
      const groupId = String(body.GroupId || BASE_GROUP_STATS_FOLDER_ID);
      const exactGroup = !body.GroupId || body.GroupId === BASE_GROUP_STATS_FOLDER_ID;
      // Agregat tygodnia ≈ suma dni aktywności z szablonu + jitter tygodnia
      const weekDays = REMAPPED_WEEK_TEMPLATE.map((day) =>
        buildDayMedian(day, groupId, weekKey, exactGroup)
      );
      const sum = (pick: (m: ReturnType<typeof buildDayMedian>) => number) =>
        Math.round(weekDays.reduce((acc, m) => acc + pick(m), 0));
      const median = {
        ...buildGroupMedian(index, benchmark),
        Distance: sum((m) => m.Distance),
        HSR: sum((m) => m.HSR),
        Sprint: sum((m) => m.Sprint),
        Acceleration: sum((m) => m.Acceleration),
        Deceleration: sum((m) => m.Deceleration),
        PlayerLoad: sum((m) => m.PlayerLoad),
        DistanceACWR: Number(
          (0.85 + Math.abs(metricJitter(`week:${weekKey}`, "DistanceACWR", 0)) / 200).toFixed(2)
        ),
        AccelerationACWR: Number(
          (0.9 + Math.abs(metricJitter(`week:${weekKey}`, "AccelerationACWR", 0)) / 200).toFixed(2)
        ),
        DecelerationACWR: Number(
          (0.88 + Math.abs(metricJitter(`week:${weekKey}`, "DecelerationACWR", 0)) / 200).toFixed(2)
        ),
        HmldACWR: Number(
          (0.92 + Math.abs(metricJitter(`week:${weekKey}`, "HmldACWR", 0)) / 200).toFixed(2)
        ),
      };

      const iso = toIsoDateOnly(cursor);
      points.push({
        StartAt: `${iso}T00:00:00.000Z`,
        EndAt: `${iso}T23:59:59.000Z`,
        SessionCount: 4,
        Median: median,
        Percentage: buildGroupPercentage(index),
        Events: { Activity: 5 },
        Event: { Activity: 5 },
      });

      cursor.setDate(cursor.getDate() + 7);
      index += 1;
    }

    return points.length ? points : buildWeeklyRemappedPoints(body, benchmark);
  }

  // season (Interval=2) — jeden punkt na miesiąc
  if (interval === 2) {
    const points: Array<{
      StartAt: string;
      EndAt: string;
      SessionCount: number;
      Median: ReturnType<typeof buildGroupMedian>;
      Percentage: ReturnType<typeof buildGroupPercentage>;
    }> = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1, 12);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1, 12);
    let index = 0;
    while (cursor <= endMonth && index < 18) {
      const monthKey = toIsoDateOnly(cursor);
      const median = buildGroupMedian(index, benchmark);
      median.Distance = Math.round(
        median.Distance + metricJitter(`month:${monthKey}`, "Distance", 0)
      );
      median.HSR = Math.round(median.HSR + metricJitter(`month:${monthKey}`, "HSR", 0));
      median.PlayerLoad = Math.round(
        median.PlayerLoad + metricJitter(`month:${monthKey}`, "PlayerLoad", 0)
      );
      points.push({
        StartAt: `${monthKey}T00:00:00.000Z`,
        EndAt: `${monthKey}T23:59:59.000Z`,
        SessionCount: 12 + (index % 4),
        Median: median,
        Percentage: buildGroupPercentage(index),
      });
      cursor.setMonth(cursor.getMonth() + 1);
      index += 1;
    }
    return points.length ? points : [ {
      StartAt: new Date().toISOString(),
      EndAt: new Date().toISOString(),
      SessionCount: 2,
      Median: buildGroupMedian(0, benchmark),
      Percentage: buildGroupPercentage(0),
    }];
  }

  // Tydzień / kilka dni — tabela dzienna jak na screenie.
  if (daysSpan <= 8) {
    return buildWeeklyRemappedPoints(body, benchmark);
  }

  // Miesiąc (~28–31 dni) — punkt na każdy dzień (aktywność wg wzorca tygodnia)
  if (daysSpan <= 40) {
    const points: Array<{
      StartAt: string;
      EndAt: string;
      SessionCount: number;
      Median: ReturnType<typeof buildDayMedian>;
      Percentage: ReturnType<typeof buildDayPercentage>;
      Events?: { Activity: number };
      Event?: { Activity: number };
    }> = [];

    const groupId = String(body.GroupId || BASE_GROUP_STATS_FOLDER_ID);
    const exactGroup = !body.GroupId || body.GroupId === BASE_GROUP_STATS_FOLDER_ID;
    const cursor = new Date(mondayOf(start));
    // Align to dateFrom even if not monday
    cursor.setTime(start.getTime());
    cursor.setHours(12, 0, 0, 0);

    while (cursor <= end) {
      const iso = toIsoDateOnly(cursor);
      const weekMonday = mondayOf(cursor);
      const weekKey = toIsoDateOnly(weekMonday);
      const dayOffset = Math.round(
        (cursor.getTime() - weekMonday.getTime()) / 86400000
      );
      const template = REMAPPED_WEEK_TEMPLATE.find((d) => d.dayOffset === dayOffset);

      if (template) {
        const median = buildDayMedian(template, groupId, weekKey, exactGroup);
        if (benchmark) {
          median.Distance = Math.round(median.Distance * 0.92);
          median.PlayerLoad = Math.round(median.PlayerLoad * 0.9);
        }
        points.push({
          StartAt: `${iso}T00:00:00.000Z`,
          EndAt: `${iso}T23:59:59.000Z`,
          SessionCount: 1,
          Median: median,
          Percentage: buildDayPercentage(median),
          Events: { Activity: template.activity },
          Event: { Activity: template.activity },
        });
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    return points;
  }

  const stepDays = 7;
  const points: Array<{
    StartAt: string;
    EndAt: string;
    SessionCount: number;
    Median: ReturnType<typeof buildGroupMedian>;
    Percentage: ReturnType<typeof buildGroupPercentage>;
  }> = [];

  const cursor = new Date(start);
  let index = 0;

  while (cursor <= end && index < 16) {
    const pointEnd = new Date(cursor);
    pointEnd.setDate(pointEnd.getDate() + stepDays);

    points.push({
      StartAt: cursor.toISOString(),
      EndAt: pointEnd.toISOString(),
      SessionCount: (index % 3) + 2,
      Median: buildGroupMedian(index, benchmark),
      Percentage: buildGroupPercentage(index),
    });

    cursor.setDate(cursor.getDate() + stepDays);
    index += 1;
  }

  if (points.length > 0) {
    return points;
  }

  const fallbackDate = new Date().toISOString();
  return [
    {
      StartAt: fallbackDate,
      EndAt: fallbackDate,
      SessionCount: 2,
      Median: buildGroupMedian(0, benchmark),
      Percentage: buildGroupPercentage(0),
    },
  ];
};

export const mockGroupMedicalCards = [
  { Weight: 61.5, Height: 174, UpdatedAt: "2026-04-15T10:00:00.000Z" },
  { Weight: 62.0, Height: 174, UpdatedAt: "2026-05-12T10:00:00.000Z" },
  { Weight: 62.4, Height: 175, UpdatedAt: "2026-06-01T10:00:00.000Z" },
  { Weight: 63.1, Height: 175, UpdatedAt: "2026-06-28T10:00:00.000Z" },
  { Weight: 63.0, Height: 175, UpdatedAt: "2026-07-10T10:00:00.000Z" },
];

export const getMockSummaryStatisticsAggregated = demoApi(async (body: StatisticsRequest = {}) =>
  buildPoints(body));

export const getMockSummaryStatisticsAggregatedAge = demoApi(async (body: StatisticsRequest = {}) =>
  buildPoints(body, true));

export const getMockGroupSummaryStatisticsAggregated = demoApi(async (
  body: StatisticsRequest = {}
) => buildGroupPoints(body));

export const getMockGroupSummaryStatisticsAggregatedAge = demoApi(async (
  body: StatisticsRequest = {}
) => buildGroupPoints(body, true));

/**
 * Osobne dane pod Statystyki1 (wykres) — nie bazują na tabeli Statystyki2.
 * Niezależne słupki per metryka + ACWR z rozjechanymi fazami.
 */
const buildChartFriendlyMedian = (slotKey: string, index: number, total: number) => {
  const t = total <= 1 ? 0.5 : index / (total - 1);
  const mi = (metric: string) => metricIntensity(slotKey, metric, t);

  const Distance = Math.round(lerp(2800, 5200, mi("Distance")));
  const HSR = Math.round(lerp(18, 65, mi("HSR")));
  const Sprint = Math.round(lerp(5, 16, mi("Sprint")));
  const Acceleration = Math.round(lerp(24, 48, mi("Acceleration")));
  const Deceleration = Math.round(lerp(20, 42, mi("Deceleration")));
  const PlayerLoad = Math.round(lerp(250, 380, mi("PlayerLoad")));
  const HMLD = Math.round(lerp(850, 1500, mi("HMLD")));
  const Power = Math.round(lerp(220, 295, mi("Power")));
  const BurnCalories = Math.round(lerp(340, 520, mi("Calories")));
  const HeartRateAverage = Math.round(lerp(128, 152, mi("HR")));
  const MaxSpeed = Number(lerp(26.5, 31.2, mi("MaxSpeed")).toFixed(2));
  const Tempo = Number(lerp(0.94, 1.26, mi("Tempo")).toFixed(2));

  // Fazy przesunięte o ~90° / 180° — linie ACWR krzyżują się w górę/dół
  const DistanceACWR = independentAcwr(slotKey, "DistanceACWR", t, 0);
  const AccelerationACWR = independentAcwr(slotKey, "AccelerationACWR", t, Math.PI);
  const DecelerationACWR = independentAcwr(slotKey, "DecelerationACWR", t, Math.PI / 2);
  const HmldACWR = independentAcwr(slotKey, "HmldACWR", t, (3 * Math.PI) / 2);

  return {
    Distance,
    DistanceACWR,
    AccelerationACWR,
    DecelerationACWR,
    MaxSpeed,
    HeartRateAverage,
    Acceleration,
    Sprint,
    Deceleration,
    HSR,
    HmldACWR,
    HMLD,
    Power,
    BurnCalories,
    IntensityPercentage: Math.min(92, Math.round(lerp(52, 85, mi("Int%")))),
    DynamicsPercentage: Math.min(92, Math.round(lerp(50, 82, mi("Dyn%")))),
    EndurancePercentage: Math.min(92, Math.round(lerp(48, 80, mi("End%")))),
    PlayerLoad,
    Tempo,
  };
};

const buildGroupChartPoints = (body: StatisticsRequest = {}) => {
  const start = parseDate(body.StartAt);
  const end = parseDate(body.EndAt);
  const interval = Number(body.Interval);
  const groupId = String(body.GroupId || "chart");

  const pushPoint = (
    iso: string,
    index: number,
    total: number,
    activity: number
  ) => {
    const slotKey = `${groupId}|${iso}`;
    const median = buildChartFriendlyMedian(slotKey, index, total);
    return {
      StartAt: `${iso}T00:00:00.000Z`,
      EndAt: `${iso}T23:59:59.000Z`,
      SessionCount: activity === 4 ? 1 : 1 + (hashSeed(slotKey) % 2),
      Median: median,
      Percentage: {
        Distance: Math.min(100, Math.round(median.Distance / 80)),
        DistanceACWR: Math.min(100, Math.round(median.DistanceACWR * 50)),
        AccelerationACWR: Math.min(100, Math.round(median.AccelerationACWR * 50)),
        DecelerationACWR: Math.min(100, Math.round(median.DecelerationACWR * 50)),
        MaxSpeed: Math.min(100, Math.round(median.MaxSpeed * 2.5)),
        HeartRateAverage: Math.min(100, Math.round(median.HeartRateAverage / 2)),
        Acceleration: Math.min(100, Math.round(median.Acceleration * 2.5)),
        Sprint: Math.min(100, Math.round(median.Sprint * 8)),
        Deceleration: Math.min(100, Math.round(median.Deceleration * 2)),
        HSR: Math.min(100, Math.round(median.HSR / 2)),
        HmldACWR: Math.min(100, Math.round(median.HmldACWR * 50)),
        HMLD: Math.min(100, Math.round(median.HMLD / 20)),
        Power: Math.min(100, Math.round(median.Power / 5)),
        BurnCalories: Math.min(100, Math.round(median.BurnCalories / 8)),
        IntensityPercentage: median.IntensityPercentage,
        DynamicsPercentage: median.DynamicsPercentage,
        EndurancePercentage: median.EndurancePercentage,
        PlayerLoad: Math.min(100, Math.round(median.PlayerLoad / 5)),
        Tempo: Math.min(100, Math.round(median.Tempo * 40)),
      },
      Events: { Activity: activity },
      Event: { Activity: activity },
    };
  };

  // fewWeeks — punkt na tydzień
  if (interval === 1) {
    const slots: string[] = [];
    const cursor = mondayOf(start);
    while (cursor <= end && slots.length < 24) {
      slots.push(toIsoDateOnly(cursor));
      cursor.setDate(cursor.getDate() + 7);
    }
    return slots.map((iso, index) => pushPoint(iso, index, slots.length, 5));
  }

  // season — punkt na miesiąc
  if (interval === 2) {
    const slots: string[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1, 12);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1, 12);
    while (cursor <= endMonth && slots.length < 18) {
      slots.push(toIsoDateOnly(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return slots.map((iso, index) => pushPoint(iso, index, slots.length, 5));
  }

  // tydzień / miesiąc / dzień — punkt na każdy dzień w zakresie
  const daySlots: string[] = [];
  const dayCursor = new Date(start);
  dayCursor.setHours(12, 0, 0, 0);
  const dayEnd = new Date(end);
  dayEnd.setHours(12, 0, 0, 0);
  while (dayCursor <= dayEnd && daySlots.length < 40) {
    daySlots.push(toIsoDateOnly(dayCursor));
    dayCursor.setDate(dayCursor.getDate() + 1);
  }

  // Lekki „oddech”: część dni słabsza / bez meczu, ale wartości nadal na wykresie
  return daySlots.map((iso, index) => {
    const dow = parseDate(iso).getDay();
    const activity = dow === 0 || dow === 6 ? 4 : 0;
    // Weekend nieco niższy przez inny seed w intensity — OK
    return pushPoint(iso, index, daySlots.length, activity);
  });
};

export const getMockGroupChartStatisticsAggregated = demoApi(async (
  body: StatisticsRequest = {}
) => buildGroupChartPoints(body));

export const getMockGroupMedicalCards = demoApi(async (_playerIds?: string) => ({
  MedicalCards: mockGroupMedicalCards,
}));
